import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { bemJoin } from 'bem-join'
import { Stack } from 'component-library-next'

import { ReactComponent as WarningCircleIcon } from '@app/assets/Small/Outline/WarningCircle.svg'
import { FormattedError } from '@app/common/Components/FormattedError'
import {
  AlertType,
  useNotifications
} from '@app/common/services/Notifications/NotificationsProvider'
import { Features } from '@app/Feature'
import { isFeatureEnabled } from '@app/Feature/Store/selectors'
import { useIntl } from '@app/intl/Intl'
import { VisuallyHidden } from '@app/lib/display/VisuallyHidden/VisuallyHidden'
import { InputField } from '@app/lib/fields/InputField/InputField'
import { Button } from '@app/lib/inputs/Button/Button'
import { Select } from '@app/lib/inputs/Select'
import { Text } from '@app/lib/typography/Text/Text'
import {
  ClaimCourtCaseChoiceField,
  ClaimCourtCaseOption
} from '@app/modules/Messages/Components/ProfileBox/CourtCase/ClaimCourtCaseChoiceField'
import { useCustomerId } from '@app/modules/Messages/hooks/useCustomerId'
import { useMessageTopicsQuery } from '@app/queries/MessageTopics/MessageTopics.queries'
import { useOpenCourtCaseMutation } from '@app/queries/OpenCourtCase/OpenCourtCase.queries'

import './styles.scss'

type CourtCaseFormProps = {
  closeDialog: () => void
  isMieterEngelCustomer: boolean
}

export const CourtCaseForm = ({ closeDialog, isMieterEngelCustomer }: CourtCaseFormProps) => {
  const methods = useForm()
  const { pushNotification } = useNotifications()
  const b = bemJoin('court-case-form')
  const { formatMessage } = useIntl()
  const { data: messageTopicsData } = useMessageTopicsQuery()
  const customerId = useCustomerId()
  const isCourtCaseProgramUser = useSelector(isFeatureEnabled(Features.COURT_CASE_PROGRAM))

  const [openCourtCase, { loading }] = useOpenCourtCaseMutation({
    onError: (error) => {
      pushNotification({
        type: AlertType.error,
        message: <FormattedError error={error} />
      })
    },
    onCompleted: () => {
      pushNotification({
        type: AlertType.success,
        message: formatMessage({ id: 'CourtCaseDialog.Notification.Success' })
      })
      closeDialog()
    }
  })

  const submit = async (data) => {
    const claimCourtCase = data.claimCourtCase
      ? data.claimCourtCase === ClaimCourtCaseOption.SelfAssign
      : undefined

    await openCourtCase({
      variables: {
        input: {
          customerId,
          topicId: data.topicId,
          claimCourtCase
        }
      }
    })
  }

  const showClaimCourtCaseChoiceField = isMieterEngelCustomer
  const showNonProgramDisclaimer = !isCourtCaseProgramUser && !isMieterEngelCustomer

  return (
    <FormProvider {...methods}>
      <form
        aria-label={formatMessage({ id: 'CourtCaseDialog.Popup.AriaLabel' })}
        onSubmit={methods.handleSubmit((data) => submit(data))}
      >
        <Stack>
          {showClaimCourtCaseChoiceField && <ClaimCourtCaseChoiceField />}
          <Text>{formatMessage({ id: 'CourtCaseDialog.Popup.Select.Topic.Label' })}</Text>
          <InputField
            name={'topicId'}
            rules={{
              required: {
                value: true,
                message: formatMessage({ id: 'CourtCaseDialog.Popup.Select.Required' })
              }
            }}
            label={(labelProps) => (
              <VisuallyHidden>
                <label {...labelProps}>topic-id</label>
              </VisuallyHidden>
            )}
            render={({ inputProps, error }) => (
              <Select
                {...inputProps}
                triggerSize='sm'
                modal={true}
                error={error}
                placeholder={formatMessage({ id: 'CourtCaseDialog.Popup.Select.Placeholder' })}
                options={
                  messageTopicsData
                    ? messageTopicsData.messageTopics.map((topic) => {
                        return {
                          value: topic.id,
                          label: topic.name
                        }
                      })
                    : []
                }
              />
            )}
          />
          {showNonProgramDisclaimer && (
            <div className={b('disclaimer')}>
              <WarningCircleIcon />
              {formatMessage({ id: 'CourtCaseDialog.Popup.NonProgramDisclaimer' })}
            </div>
          )}
          <Button
            type='submit'
            color={'secondary'}
            variant={'solid'}
            loading={loading}
            disabled={!methods.formState.isValid}
          >
            {formatMessage({ id: 'CourtCaseDialog.Pupup.Button.Confirm' })}
          </Button>
          <Button color={'secondary'} variant={'outlined'} onClick={closeDialog}>
            {formatMessage({ id: 'CourtCaseDialog.Pupup.Button.Cancel' })}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  )
}
